import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { StoreModule } from '@ngrx/store';
import { EffectsModule } from '@ngrx/effects';
import * as fromSalesDocuments from './+state/sales-documents.reducer';
import { SalesDocumentsEffects } from './+state/sales-documents.effects';
import { SalesDocumentsListComponent } from './sales-documents-list/sales-documents-list.component';
import { SalesDocumentComponent } from './sales-document/sales-document.component';
import { SyncService } from './sync.service';
import {
    IconRegistryService,
    iccIconNavigateBefore,
    iccIconNavigateNext,
    iccIconDelete,
    iccIconCopy,
    iccIconCheckBoxIndeterminated,
    iccIconCheckBoxUnchecked,
    iccIconCheckBoxChecked,
    iccIconEdit,
    iccIconMarginMarkup,
    UiModule,
    iccIconAdd,
    iccIconMoreHorizontal,
    iccIconMoreVertical,
    iccIconCopyOut,
    iccIconDatePicker,
    iccIconAlternative,
    iccIconRadioButtonChecked,
    iccIconRadioButtonUnchecked,
    iccIconOpen,
    iccIconSearch,
    iccIconSettings,
    iccIconConfigure,
    iccIconList,
    iccIconClose,
    iccIconSync,
    iccIconDocument,
    iccIconDone,
    iccIconRemove,
    iccIconExpandMore,
    iccIconCompress,
    iccIconExpandLess,
    iccIconExpand,
    iccIconInfo,
    iccIconFileSynced,
    iccIconFileSynchronization,
    iccIconFileDownload,
    iccIconFileOnlineOnly,
    iccIconFileOfflineOnly,
    iccIconState,
    iccIconReplay,
    iccIconVisible,
    iccIconLink,
    iccIconCalculate,
    iccIconSparePartsCopyOut,
    iccIconComment,
    iccIconArrowForward,
    iccIconDownload
} from '@icc/ui';

import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SalesDocumentSummaryComponent } from './sales-document-summary/sales-document-summary.component';
import { TranslateModule } from '@ngx-translate/core';
import { B2BSharedModule } from 'libs/b2b/shared/src/lib/shared.module';
import { ClientDialogComponent } from './client-dialog/client-dialog.component';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { SalesDocumentPositionComponent } from './sales-document-position/sales-document-position.component';
import { SalesDocumentAttachmentsComponent } from './sales-document-attachments/sales-document-attachments.component';
import { SharedModule as ConfiguratorSharedModule } from '@icc/configurator/shared';
import { PricesDataService, PricesDataSharedService, SharedModule } from '@icc/shared';
import { SalesDocumentFiltersComponent } from './sales-document-filters/sales-document-filters.component';
import { PositionPricesDialogComponent } from './position-prices-dialog/position-prices-dialog.component';
import { ChangeUnitDialogComponent } from './change-unit-dialog/change-unit-dialog.component';
import { AddedProductDialogComponent } from './added-product-dialog/added-product-dialog.component';
import { SalesDocumentAccessGuard } from './sales-document-access-guard';
import { RouterModule } from '@angular/router';
import { SortOrderIconComponent } from '../../../shared/src/lib/sort-order-icon/sort-order-icon.component';
import { ConfiguratorSketchModule } from '@icc/configurator/sketch';
import { SellerDialogComponent } from './seller-dialog/seller-dialog.component';
import { ClientPreviewDialogComponent } from './client-preview-dialog/client-preview-dialog.component';
import { SalesDocumentsOperationsEffects } from './+state/sales-documents-operations.effects';
import { DocumentStatusComponent } from './document-status/document-status.component';
import { SalesDocumentDetailsComponent } from './sales-document-details/sales-document-details.component';
import {
    DocumentFlagsComponent,
    IccFlagElementComponentDirective,
} from './document-flags/document-flags.component';
import { StaticFlagComponent } from './document-flags/static-flag.component';
import { DocumentStatusInfoDialogComponent } from './document-status-info-dialog/document-status-info-dialog.component';
import { DocumentDetailComponent } from './document-detail/document-detail.component';
import { DocumentDetailContentDirective } from './document-detail/document-detail-content.directive';
import { CreateOrderDialogComponent } from './create-order-dialog/create-order-dialog.component';
import { ConfirmOrderDialogComponent } from './confirm-order-dialog/confirm-order-dialog.component';
import { PriceVersionSelectorComponent } from './price-version-selector/price-version-selector.component';
import { OwnProductsDialogComponent } from './own-products-dialog/own-products-dialog.component';
import { DocumentTaxDialogComponent } from './document-tax-dialog/document-tax-dialog.component';
import { CannotAddProductDialogComponent } from './cannot-add-product-dialog/cannot-add-product-dialog.component';
import { LoaderComponent } from './loader/loader.component';
import { SyncedIndicatorComponent } from './synced-indicator/synced-indicator.component';
import { DocumentHistoryDialogComponent } from './document-history-dialog/document-history-dialog.component';
import { SendValuationDialogComponent } from './send-valuation-dialog/send-valuation-dialog.component';
import { ModifyOrderDialogComponent } from './modify-order-dialog/modify-order-dialog.component';
import { CancelOrderDialogComponent } from './cancel-order-dialog/cancel-order-dialog.component';
import { DocumentManagerService } from './document-manager.service';
import { B2bDocumentManagerService } from './b2b-document-manager.service';
import { B2bDocumentPricesService } from './b2b-document-prices.service';
import { DocumentPricesService } from './document-prices.service';
import { SyncingMonitorDirective } from './syncing-monitor.directive';
import { PriceVersionDialogComponent } from './price-version-dialog/price-version-dialog.component';
import { PrintTemplatedDialogComponent } from './print-templated-dialog/print-templated-dialog.component';
import { DeliverySignaturePreviewComponent } from './delivery-signature-preview/delivery-signature-preview.component';
import { PrintSettingsDialogComponent } from './print-settings-dialog/print-settings-dialog.component';

@NgModule({
    imports: [
        CommonModule,
        RouterModule,
        TranslateModule,
        ReactiveFormsModule,
        MatCardModule,
        MatIconModule,
        MatButtonModule,
        NgxPermissionsModule,
        NgxPaginationModule,
        UiModule,
        FormsModule,
        StoreModule.forFeature(
            fromSalesDocuments.SALES_DOCUMENTS_FEATURE_KEY,
            fromSalesDocuments.reducer
        ),
        EffectsModule.forFeature([SalesDocumentsEffects, SalesDocumentsOperationsEffects]),
        SharedModule,
        ConfiguratorSharedModule,
        B2BSharedModule,
        DragDropModule,
        ConfiguratorSketchModule,
    ],
    declarations: [
        SalesDocumentsListComponent,
        SalesDocumentComponent,
        SalesDocumentSummaryComponent,
        ClientDialogComponent,
        SalesDocumentPositionComponent,
        SalesDocumentAttachmentsComponent,
        SalesDocumentFiltersComponent,
        PositionPricesDialogComponent,
        ChangeUnitDialogComponent,
        AddedProductDialogComponent,
        CannotAddProductDialogComponent,
        SellerDialogComponent,
        ClientPreviewDialogComponent,
        DocumentStatusComponent,
        SalesDocumentDetailsComponent,
        DocumentFlagsComponent,
        StaticFlagComponent,
        DocumentStatusInfoDialogComponent,
        IccFlagElementComponentDirective,
        DocumentDetailComponent,
        DocumentDetailContentDirective,
        CreateOrderDialogComponent,
        ConfirmOrderDialogComponent,
        PriceVersionSelectorComponent,
        OwnProductsDialogComponent,
        DocumentTaxDialogComponent,
        SyncedIndicatorComponent,
        LoaderComponent,
        DocumentHistoryDialogComponent,
        SendValuationDialogComponent,
        ModifyOrderDialogComponent,
        CancelOrderDialogComponent,
        SyncingMonitorDirective,
        PriceVersionDialogComponent,
        PrintTemplatedDialogComponent,
        DeliverySignaturePreviewComponent,
        PrintSettingsDialogComponent
    ],
    providers: [
        SyncService,
        SalesDocumentAccessGuard,
        B2bDocumentManagerService,
        B2bDocumentPricesService,
        { provide: DocumentManagerService, useExisting: B2bDocumentManagerService },
        { provide: DocumentPricesService, useExisting: B2bDocumentPricesService },
        { provide: PricesDataSharedService, useExisting: PricesDataService },
    ],
    exports: [
        ClientDialogComponent,
        ChangeUnitDialogComponent,
        SellerDialogComponent,
        ClientPreviewDialogComponent,
        DocumentStatusComponent,
        DocumentFlagsComponent,
        DocumentStatusInfoDialogComponent,
        IccFlagElementComponentDirective,
        DocumentDetailComponent,
        CreateOrderDialogComponent,
        ConfirmOrderDialogComponent,
        SyncedIndicatorComponent,
        LoaderComponent,
        DocumentHistoryDialogComponent,
        SendValuationDialogComponent,
        ModifyOrderDialogComponent,
        CancelOrderDialogComponent,
        PriceVersionDialogComponent,
        PrintTemplatedDialogComponent,
        SalesDocumentPositionComponent,
        PrintSettingsDialogComponent
    ],
})
export class B2bSalesDocumentModule {
    constructor(private iconRegistry: IconRegistryService) {
        this.iconRegistry.registerIcons([
            iccIconNavigateBefore,
            iccIconNavigateNext,
            iccIconDelete,
            iccIconCopy,
            iccIconCheckBoxIndeterminated,
            iccIconCheckBoxUnchecked,
            iccIconCheckBoxChecked,
            iccIconEdit,
            iccIconMarginMarkup,
            iccIconAdd,
            iccIconMoreHorizontal,
            iccIconMoreVertical,
            iccIconCopyOut,
            iccIconDatePicker,
            iccIconAlternative,
            iccIconRadioButtonChecked,
            iccIconRadioButtonUnchecked,
            iccIconOpen,
            iccIconSearch,
            iccIconSettings,
            iccIconConfigure,
            iccIconList,
            iccIconClose,
            iccIconSync,
            iccIconDocument,
            iccIconDone,
            iccIconRemove,
            iccIconExpandMore,
            iccIconCompress,
            iccIconExpandLess,
            iccIconExpand,
            iccIconInfo,
            iccIconFileSynced,
            iccIconFileSynchronization,
            iccIconFileDownload,
            iccIconFileOnlineOnly,
            iccIconFileOfflineOnly,
            iccIconState,
            iccIconReplay,
            iccIconVisible,
            iccIconLink,
            iccIconCalculate,
            iccIconSparePartsCopyOut,
            iccIconComment,
            iccIconArrowForward,
            iccIconDownload,
        ]);
    }
}
